import { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, X, Send, Loader2 } from "lucide-react";

const SUGGESTIONS = [
  "What should I build?",
  "Find me a SaaS opportunity for plumbers",
  "Give me 5 AI ideas with low competition",
  "How do I get my first 10 customers?",
];

function renderMarkdown(text) {
  const html = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^### (.*)$/gm, '<h4 class="font-semibold text-slate-100 mt-2">$1</h4>')
    .replace(/^- (.*)$/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/\n/g, "<br/>");
  return { __html: DOMPurify.sanitize(html, { ALLOWED_TAGS: ["strong", "h4", "li", "br", "ul", "ol", "em", "code", "p"], ALLOWED_ATTR: ["class"] }) };
}

export default function AIAssistant() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      api.get("/ai/chat/history").then((r) => setMessages(r.data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  if (!user) return null;

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await api.post("/ai/chat", { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: e.response?.data?.detail || "Something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ai-assistant-open"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-indigo-500 flex items-center justify-center shadow-2xl glow-cyan hover:scale-105 transition-transform">
          <Sparkles className="w-6 h-6 text-slate-950" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[min(92vw,400px)] h-[70vh] glass rounded-2xl flex flex-col overflow-hidden border border-cyan-500/20 shadow-2xl fade-up" data-testid="ai-assistant-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-indigo-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-slate-950" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-100 leading-none">Nova</p>
                <p className="text-[10px] text-slate-500">Your AI Builder Coach</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-200" data-testid="ai-assistant-close"><X className="w-5 h-5" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && !loading && (
              <div className="text-sm text-slate-400">
                <p className="mb-3">Hi {user.name?.split(" ")[0]} 👋 I'm Nova. Ask me what to build, or how to validate and launch.</p>
                <div className="space-y-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:border-cyan-500/40 text-slate-300 transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-cyan-500/15 text-cyan-50 border border-cyan-500/20" : "bg-white/5 text-slate-200 border border-white/8"}`}>
                  <div dangerouslySetInnerHTML={renderMarkdown(m.content)} />
                </div>
              </div>
            ))}
            {loading && <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" />Nova is thinking…</div>}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/8 flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Nova anything…"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-cyan-500/50" data-testid="ai-assistant-input" />
            <button type="submit" disabled={loading} className="w-9 h-9 rounded-lg bg-cyan-500 hover:bg-cyan-400 flex items-center justify-center text-slate-950 disabled:opacity-50" data-testid="ai-assistant-send"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
}
